import { Polygon } from 'geojson';
import { useEffect, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
// @ts-ignore
import MapboxCircle from 'mapbox-gl-circle';
import { useRecoilState } from 'recoil';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { mapNewCircleState } from '../../../storage/map';
import { Point } from '../../../types/geoModels';

type Props = Readonly<{
  onUpdateGeoreference?: (point: Point) => void;
  onUpdatePolygon: (newPolygon?: Polygon) => void;
}>;

function CoreCircleControl({ onUpdateGeoreference, onUpdatePolygon }: Props) {
  const { map } = useMap();
  const [circleObject, setCircleObject] = useState<MapboxCircle>();
  const [centerChanged, setCenterChanged] = useState(0);
  const [newCircle, setNewCircle] = useRecoilState(mapNewCircleState);

  const updateWithAddress = async () => {
    if (circleObject?.center) {
      const point = {
        lng: circleObject?.center?.[0],
        lat: circleObject?.center?.[1],
      };
      onUpdateGeoreference?.(point);
    }
  };

  useUpdateEffect(() => {
    if (!newCircle) return;
    const center = { lat: newCircle.lat, lng: newCircle.lng };
    if (!circleObject) {
      setCircleObject(
        new MapboxCircle(center, newCircle.radius, {
          editable: true,
          fillColor: '#FCC0D0',
          fillOpacity: 0.5,
          strokeColor: '#000',
          strokeOpacity: 1,
          strokeWeight: 3,
          refineStroke: true,
        }),
      );
    }
  }, [newCircle]);

  useUpdateEffect(() => {
    onUpdatePolygon(circleObject?._circle?.geometry);
    updateWithAddress();
  }, [circleObject?.radius, circleObject?.center]);

  useUpdateEffect(() => {
    updateWithAddress();
  }, [centerChanged]);

  useEffect(() => {
    if (!circleObject || !map) {
      return;
    }
    const circleObjRef = circleObject;

    const onCenterChanged = async (circle: MapboxCircle) => {
      const lat: number = circle.center[1];
      const lng: number = circle.center[0];
      setNewCircle((prev) => ({ ...prev!, lat, lng }));
      setCenterChanged((prev) => prev + 1);
    };

    const onRadiusChanged = (circle: MapboxCircle) => {
      setNewCircle((prev) => ({ ...prev!, radius: circle.radius }));
    };

    circleObjRef.addTo(map.getMap());
    circleObjRef.on('centerchanged', onCenterChanged);
    circleObjRef.on('radiuschanged', onRadiusChanged);

    return () => {
      circleObjRef.off('centerchanged', onCenterChanged);
      circleObjRef.off('radiuschanged', onRadiusChanged);
      circleObjRef.remove();
    };
  }, [circleObject, map]);

  return null;
}

export default CoreCircleControl;
